/**
 * 运营指挥中心右侧展示页面
 * 全屏、深色主题、定时刷新数据
 *
 * 核心功能：
 * 1. 世界时钟展示（UTC、墨西哥城、冰岛、布达佩斯、布鲁塞尔、哈萨克斯坦、北京）
 * 2. 航站运行监控（气象METAR/TAF、NOTAM、ULD库存）
 * 3. 凤凰卫视24小时直播
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { message } from "antd";
import useInterval from "../../../hooks/useInterval";
import { screenApi } from "../../../api/screenApi";
import "./ScreenRightPage.css";

dayjs.extend(utc);
dayjs.extend(timezone);

// 机场信息配置
const AIRPORTS = {
    bud: { icao: 'LHBP', iata: 'BUD', name: '布达佩斯', timezone: 'Europe/Budapest' },
    lgg: { icao: 'EBLG', iata: 'LGG', name: '列日', timezone: 'Europe/Brussels' },
    cgo: { icao: 'ZHCC', iata: 'CGO', name: '郑州', timezone: 'Asia/Shanghai' },
    hkg: { icao: 'VHHH', iata: 'HKG', name: '香港', timezone: 'Asia/Hong_Kong' },
    hsa: { icao: 'UAIT', iata: 'HSA', name: '土耳其斯坦', timezone: 'Asia/Almaty' },
    nqz: { icao: 'UACC', iata: 'NQZ', name: '努尔苏丹', timezone: 'Asia/Almaty' }
};

// 轮播页面配置
const CAROUSEL_PAGES = [
    ['bud', 'lgg'],  // 第一页：BUD, LGG
    ['cgo', 'hkg'],  // 第二页：CGO, HKG
    ['hsa', 'nqz']   // 第三页：HSA, NQZ
];

// 气象数据通过后端API代理获取，无需单独启动代理服务

const ScreenRightPage = () => {
    // ===== 状态管理 =====
    const [currentTime, setCurrentTime] = useState(dayjs());
    const [loading, setLoading] = useState(true);
    const [carouselIndex, setCarouselIndex] = useState(0);
    
    // 数据状态
    const [stationData, setStationData] = useState({});  // 航站信息（NOTAM、ULD）
    const [weatherData, setWeatherData] = useState({});  // 气象数据
    const [localTimes, setLocalTimes] = useState({});    // 各站点本地时间
    
    // NOTAM滚动状态管理
    const notamStatesRef = useRef({});

    // ===== 时钟相关 =====
    const [clockTimes, setClockTimes] = useState({
        utc: '',
        mex: '',
        isl: '',
        bud: '',
        bru: '',
        hsa: '',
        bjs: ''
    });

    // 更新时钟
    const updateClocks = useCallback(() => {
        const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
        
        setClockTimes({
            utc: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Etc/UTC' }),
            mex: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'America/Mexico_City' }),
            isl: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Atlantic/Reykjavik' }),
            bud: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Europe/Budapest' }),
            bru: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Europe/Brussels' }),
            hsa: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Asia/Almaty' }),
            bjs: new Date().toLocaleTimeString('en-GB', { ...timeOptions, timeZone: 'Asia/Shanghai' })
        });
    }, []);

    // 更新各站点本地时间
    const updateLocalTimes = useCallback(() => {
        const times = {};
        Object.entries(AIRPORTS).forEach(([code, info]) => {
            times[code] = new Date().toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: info.timezone,
                hour12: false
            });
        });
        setLocalTimes(times);
    }, []);

    // ===== 数据获取 =====
    
    // 获取航站信息（NOTAM + ULD）
    const fetchStationData = useCallback(async () => {
        try {
            const stationCodes = Object.values(AIRPORTS).map(a => a.iata).join(',');
            const response = await screenApi.getRightPanelStationInfo({ station_codes: stationCodes });
            
            if (response.data?.items) {
                const dataMap = {};
                response.data.items.forEach(item => {
                    const code = Object.keys(AIRPORTS).find(
                        k => AIRPORTS[k].iata === item.iata_code
                    );
                    if (code) {
                        dataMap[code] = item;
                    }
                });
                setStationData(dataMap);
            }
            return true;
        } catch (error) {
            console.error("[右侧大屏] 获取航站信息失败:", error);
            return false;
        }
    }, []);

    // 获取气象数据
    const fetchWeatherData = useCallback(async () => {
        try {
            const icaoCodes = Object.values(AIRPORTS).map(a => a.icao).join(',');
            
            // 并行获取METAR和TAF（通过后端API代理）
            const [metarResponse, tafResponse] = await Promise.all([
                screenApi.getMetar(icaoCodes).catch(() => ({ data: [] })),
                screenApi.getTaf(icaoCodes).catch(() => ({ data: [] }))
            ]);
            
            const metarRes = metarResponse.data || [];
            const tafRes = tafResponse.data || [];
            
            const weather = {};
            
            // 处理METAR
            if (Array.isArray(metarRes)) {
                metarRes.forEach(metar => {
                    const code = Object.keys(AIRPORTS).find(
                        k => AIRPORTS[k].icao === metar.icaoId
                    );
                    if (code) {
                        weather[code] = {
                            ...weather[code],
                            metar: parseMetar(metar),
                            temp: metar.temp
                        };
                    }
                });
            }
            
            // 处理TAF
            if (Array.isArray(tafRes)) {
                tafRes.forEach(taf => {
                    const code = Object.keys(AIRPORTS).find(
                        k => AIRPORTS[k].icao === taf.icaoId
                    );
                    if (code) {
                        weather[code] = {
                            ...weather[code],
                            taf: parseTaf(taf)
                        };
                    }
                });
            }
            
            setWeatherData(weather);
            return true;
        } catch (error) {
            console.error("[右侧大屏] 获取气象数据失败:", error);
            return false;
        }
    }, []);

    // 解析METAR - 直接返回原始报文
    const parseMetar = (metar) => {
        if (!metar || !metar.rawOb) return 'N/A';
        return metar.rawOb;
    };

    // 解析TAF - 直接返回原始报文（去掉TAF头部）
    const parseTaf = (taf) => {
        if (!taf || !taf.rawTAF) return 'N/A';
        return taf.rawTAF.replace(/^TAF\s+/, '');
    };

    // 格式化NOTAM日期
    const formatNotamDate = (dateStr) => {
        if (!dateStr) return '';
        const match = dateStr.match(/(\d{2})\/(\d{2})\/(\d{4})\s*(\d{2})(\d{2})/);
        if (match) {
            const [, month, day, , hour, min] = match;
            return `${month}/${day} ${hour}:${min}`;
        }
        return dateStr;
    };

    // 获取所有数据
    const fetchAllData = useCallback(async (isInitial = false) => {
        if (isInitial) {
            setLoading(true);
        }
        
        console.log("[右侧大屏] 刷新数据...");
        
        try {
            await Promise.all([
                fetchStationData(),
                fetchWeatherData()
            ]);
        } catch (error) {
            console.error("[右侧大屏] 刷新数据失败:", error);
            if (isInitial) {
                message.error("数据加载失败");
            }
        } finally {
            if (isInitial) {
                setLoading(false);
            }
        }
    }, [fetchStationData, fetchWeatherData]);

    // ===== 定时任务 =====
    
    // 每秒更新时钟
    useInterval(updateClocks, 1000, true);
    
    // 每分钟更新本地时间
    useInterval(updateLocalTimes, 60000, true);
    
    // 每分钟更新当前时间
    useInterval(() => setCurrentTime(dayjs()), 60000, true);
    
    // 30秒刷新数据
    useInterval(() => fetchAllData(false), 30000, false);
    
    // 30秒切换轮播页
    useInterval(() => {
        setCarouselIndex(prev => (prev + 1) % CAROUSEL_PAGES.length);
    }, 30000, false);

    // ===== 初始化 =====
    useEffect(() => {
        updateClocks();
        updateLocalTimes();
        fetchAllData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ===== METAR/TAF 滚动检测 =====
    useEffect(() => {
        // 延迟检测，确保DOM已更新
        const setupScrolling = () => {
            // 获取当前显示的页面的机场代码
            const currentPage = CAROUSEL_PAGES[carouselIndex] || [];
            
            currentPage.forEach(code => {
                ['metar', 'taf'].forEach(type => {
                    const el = document.getElementById(`${type}-${code}`);
                    if (!el) return;
                    
                    const text = el.textContent || '';
                    el.classList.remove('scrolling');
                    
                    // 测量宽度
                    setTimeout(() => {
                        const containerWidth = el.offsetWidth;
                        const textWidth = el.scrollWidth;
                        
                        if (textWidth > containerWidth && text !== 'Loading...') {
                            // 需要滚动 - 用wrapper包裹两份内容实现无缝循环
                            const scrollDuration = Math.max(textWidth / 50, 8);
                            el.innerHTML = `<span class="scroll-wrapper"><span class="scroll-text">${text}</span><span class="scroll-text">${text}</span></span>`;
                            el.style.setProperty('--scroll-duration', `${scrollDuration}s`);
                            el.classList.add('scrolling');
                        }
                    }, 100);
                });
            });
        };

        setupScrolling();
    }, [weatherData, carouselIndex]);

    // ===== NOTAM动画逻辑 =====
    useEffect(() => {
        const notamStates = notamStatesRef.current;

        // 开始NOTAM动画（水平滚动）
        const startNotamAnimation = (pageCode) => {
            const state = notamStates[pageCode];
            if (!state || state.notams.length === 0) return;

            const scroller = document.getElementById(`notam-scroller-${pageCode}`);
            const container = document.getElementById(`notam-container-${pageCode}`);
            if (!scroller || !container) {
                console.log(`[NOTAM] ${pageCode}: 找不到DOM元素`);
                return;
            }

            const items = scroller.querySelectorAll('.notam-alert-item');
            if (items.length === 0) return;

            const currentItem = items[state.currentIndex];
            const textWrapper = currentItem?.querySelector('.notam-text-wrapper');
            if (!textWrapper) {
                console.log(`[NOTAM] ${pageCode}: 找不到textWrapper`);
                return;
            }

            // 重置之前的滚动状态
            textWrapper.classList.remove('scrolling');
            textWrapper.style.transform = '';

            // 使用 requestAnimationFrame 确保DOM已更新
            requestAnimationFrame(() => {
                // 获取容器和文字宽度
                const contentEl = container.querySelector('.notam-alert-content');
                const containerWidth = contentEl ? contentEl.offsetWidth : 200;
                const textWidth = textWrapper.scrollWidth;

                console.log(`[NOTAM] ${pageCode}: 文字宽度=${textWidth}, 容器宽度=${containerWidth}`);

                // 如果文字超过容器宽度，需要水平滚动
                if (textWidth > containerWidth + 10) {
                    const scrollDistance = textWidth - containerWidth + 20;
                    const scrollDuration = Math.max(scrollDistance / 60, 4); // 每秒60px，最少4秒

                    console.log(`[NOTAM] ${pageCode}: 开始滚动，距离=${scrollDistance}px，时长=${scrollDuration.toFixed(1)}s`);

                    // 设置CSS变量
                    textWrapper.style.setProperty('--scroll-duration', `${scrollDuration}s`);
                    textWrapper.style.setProperty('--container-width', `${containerWidth}px`);
                    
                    // 强制重排后添加scrolling类
                    void textWrapper.offsetWidth;
                    textWrapper.classList.add('scrolling');
                    state.isScrolling = true;

                    // 滚动完成后切换到下一条
                    state.timer = setTimeout(() => {
                        textWrapper.classList.remove('scrolling');
                        textWrapper.style.transform = '';
                        state.isScrolling = false;
                        switchToNextNotam(pageCode);
                    }, scrollDuration * 1000 + 800);
                } else {
                    // 文字不需要滚动，停留4秒后切换
                    console.log(`[NOTAM] ${pageCode}: 无需滚动，4秒后切换`);
                    state.timer = setTimeout(() => {
                        switchToNextNotam(pageCode);
                    }, 4000);
                }
            });
        };

        // 切换到下一条NOTAM（垂直切换）
        const switchToNextNotam = (pageCode) => {
            const state = notamStates[pageCode];
            if (!state) return;
            
            // 只有1条时，重新开始动画循环
            if (state.notams.length <= 1) {
                if (state.notams.length === 1) {
                    state.timer = setTimeout(() => startNotamAnimation(pageCode), 2000);
                }
                return;
            }

            const scroller = document.getElementById(`notam-scroller-${pageCode}`);
            if (!scroller) return;

            const items = scroller.querySelectorAll('.notam-alert-item');
            const currentItem = items[state.currentIndex];
            const nextIndex = (state.currentIndex + 1) % state.notams.length;
            const nextItem = items[nextIndex];

            console.log(`[NOTAM] ${pageCode}: 垂直切换 ${state.currentIndex + 1} -> ${nextIndex + 1}`);

            // 当前条目向上滑出
            if (currentItem) {
                currentItem.classList.remove('active');
                currentItem.classList.add('leaving');
            }

            // 下一条目从下方滑入
            if (nextItem) {
                nextItem.classList.add('entering');
            }

            // 动画完成后清理状态
            setTimeout(() => {
                if (currentItem) {
                    currentItem.classList.remove('leaving');
                }
                if (nextItem) {
                    nextItem.classList.remove('entering');
                    nextItem.classList.add('active');
                }

                state.currentIndex = nextIndex;
                state.isScrolling = false;

                // 上下滚动完成后，停留2秒再开始水平滚动
                state.timer = setTimeout(() => startNotamAnimation(pageCode), 2000);
            }, 500);
        };

        // 初始化所有页面的NOTAM动画
        const initNotamAnimations = () => {
            // 清除所有之前的定时器
            Object.values(notamStates).forEach(state => {
                if (state?.timer) clearTimeout(state.timer);
            });

            CAROUSEL_PAGES.flat().forEach(pageCode => {
                const station = stationData[pageCode];
                if (station?.notams?.length > 0) {
                    console.log(`[NOTAM] ${pageCode}: 初始化，共 ${station.notams.length} 条`);
                    
                    notamStates[pageCode] = {
                        currentIndex: 0,
                        notams: station.notams,
                        isScrolling: false,
                        timer: null
                    };

                    // 设置第一条为active
                    const scroller = document.getElementById(`notam-scroller-${pageCode}`);
                    if (scroller) {
                        const items = scroller.querySelectorAll('.notam-alert-item');
                        items.forEach((item, idx) => {
                            item.classList.remove('active', 'leaving', 'entering', 'scrolling');
                            const wrapper = item.querySelector('.notam-text-wrapper');
                            if (wrapper) {
                                wrapper.classList.remove('scrolling');
                                wrapper.style.transform = '';
                            }
                            if (idx === 0) item.classList.add('active');
                        });
                    }

                    // 延迟2秒开始动画，确保DOM已完全渲染
                    notamStates[pageCode].timer = setTimeout(() => startNotamAnimation(pageCode), 2000);
                }
            });
        };

        // 延迟初始化，确保DOM已渲染
        const initTimer = setTimeout(initNotamAnimations, 500);

        // 清理函数
        return () => {
            clearTimeout(initTimer);
            Object.values(notamStates).forEach(state => {
                if (state?.timer) clearTimeout(state.timer);
            });
        };
    }, [stationData]);

    // ===== 渲染时钟数字 =====
    const renderFlipDigit = (value) => {
        return (
            <div className="flip-digit">
                <div className="flip-card">
                    <div className="flip-card-top" data-value={value}></div>
                    <div className="flip-card-bottom" data-value={value}></div>
                    <div className="flip-card-top-flip" data-value={value}></div>
                    <div className="flip-card-bottom-flip" data-value={value}></div>
                </div>
            </div>
        );
    };

    const renderFlipTime = (timeStr) => {
        if (!timeStr) return null;
        return (
            <div className="flip-time">
                {timeStr.split('').map((char, idx) => (
                    char === ':' ? (
                        <div key={idx} className="flip-separator">:</div>
                    ) : (
                        <React.Fragment key={idx}>{renderFlipDigit(char)}</React.Fragment>
                    )
                ))}
            </div>
        );
    };

    // ===== 渲染航站卡片 =====
    const renderStationCard = (code) => {
        const airport = AIRPORTS[code];
        const station = stationData[code] || {};
        const weather = weatherData[code] || {};
        const localTime = localTimes[code] || '--:--';
        
        return (
            <div className="station-info-card" key={code}>
                {/* 顶部：航站名称 + 状态 + ULD */}
                <div className="station-header">
                    <div className="station-name">
                        <span className="iata-code">{airport.iata}</span>
                        <span className="city-name">{airport.name}</span>
                    </div>
                    <div className="station-status">
                        <div className="uld-inventory">
                            <span className="uld-label">ULD:</span>
                            <span className="uld-count">{station.uld_count ?? '--'}</span>
                        </div>
                        <span className="local-time">{localTime}</span>
                        <span className="temp">
                            {weather.temp !== undefined ? `${Math.round(weather.temp)}℃` : '--℃'}
                        </span>
                    </div>
                </div>
                
                {/* METAR */}
                <div className="station-metar">
                    <span className="label">METAR</span>
                    <div className="metar-content" id={`metar-${code}`}>
                        {weather.metar || 'Loading...'}
                    </div>
                </div>
                
                {/* TAF */}
                <div className="station-taf">
                    <span className="label">TAF</span>
                    <div className="taf-content" id={`taf-${code}`}>
                        {weather.taf || 'Loading...'}
                    </div>
                </div>
                
                {/* 底部：NOTAM */}
                <div className="station-footer">
                    <div 
                        id={`notam-container-${code}`}
                        className={`notam-alert-container ${station.notams?.length ? '' : 'hidden'}`}
                    >
                        <div className="notam-alert-icon">⚠</div>
                        <div className="notam-alert-content">
                            <div className="notam-alert-scroller" id={`notam-scroller-${code}`}>
                                {station.notams?.slice(0, 5).map((notam, idx) => (
                                    <div key={idx} className={`notam-alert-item ${idx === 0 ? 'active' : ''}`}>
                                        <div className="notam-text-wrapper">
                                            <span className="notam-time">
                                                [{formatNotamDate(notam.startDate)} - {formatNotamDate(notam.endDate)}]
                                            </span>
                                            <span className="notam-condition">
                                                {(notam.condition || '').replace(/\n/g, ' ').trim()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ===== 渲染 =====
    return (
        <div className="screen-right-page sidebar-body">
            <div className="sidebar-container right-container">
                {/* 顶部时钟栏 */}
                <header className="top-bar">
                    <div className="world-clocks">
                        <div className="flip-clock">
                            <span className="city">UTC</span>
                            {renderFlipTime(clockTimes.utc)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">墨西哥城</span>
                            {renderFlipTime(clockTimes.mex)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">冰岛</span>
                            {renderFlipTime(clockTimes.isl)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">布达佩斯</span>
                            {renderFlipTime(clockTimes.bud)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">布鲁塞尔</span>
                            {renderFlipTime(clockTimes.bru)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">哈萨克斯坦</span>
                            {renderFlipTime(clockTimes.hsa)}
                        </div>
                        <div className="flip-clock">
                            <span className="city">北京</span>
                            {renderFlipTime(clockTimes.bjs)}
                        </div>
                    </div>
                </header>

                {/* 主内容区 */}
                <aside className="right-panel">
                    {/* 航站运行监控 */}
                    <div className="panel-section">
                        <h2 className="section-title">航站运行监控</h2>
                        {loading ? (
                            <div className="loading-placeholder">数据加载中...</div>
                        ) : (
                            <div className="station-carousel">
                                {CAROUSEL_PAGES.map((pageCodes, pageIdx) => (
                                    <div 
                                        key={pageIdx}
                                        className={`carousel-page ${pageIdx === carouselIndex ? 'active' : ''} ${
                                            pageIdx === (carouselIndex - 1 + CAROUSEL_PAGES.length) % CAROUSEL_PAGES.length ? 'leaving' : ''
                                        }`}
                                    >
                                        {pageCodes.map(code => renderStationCard(code))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* 凤凰卫视24小时直播 */}
                    <div className="panel-section stretch">
                        <h2 className="section-title">凤凰卫视 24小时直播</h2>
                        <div className="weather-map-placeholder">
                            <iframe 
                                style={{ width: '100%', height: '100%' }} 
                                src="https://www.youtube.com/embed/fN9uYWCjQaw?rel=0&modestbranding=1" 
                                title="凤凰卫视 24小时直播" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                referrerPolicy="strict-origin-when-cross-origin" 
                                allowFullScreen
                            />
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default ScreenRightPage;
